import { ChevronRight, Play, Trash2Icon, Zap, Link2 } from "lucide-react";
import { Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useState } from "react";
import { useDeleteApiChain, useDeleteTcFromChain, useRunApiChain } from "../../../useQuery";
import { useNotification } from "../../../hooks";
import { useQueryClient } from "@tanstack/react-query";

const METHOD_COLOR = {
  GET: "#16a34a",
  POST: "#2563eb",
  PUT: "#d97706",
  PATCH: "#9333ea",
  DELETE: "#dc2626",
};

const METHOD_BG = {
  GET: "#f0fdf4",
  POST: "#eff6ff",
  PUT: "#fffbeb",
  PATCH: "#faf5ff",
  DELETE: "#fef2f2",
};

const MethodPill = ({ method }) => {
  const color = METHOD_COLOR[method] ?? "#6b7280";
  const bg = METHOD_BG[method] ?? "#f3f4f6";
  return (
    <span
      style={{
        color,
        backgroundColor: bg,
        fontSize: "12px",
        fontWeight: 700,
        letterSpacing: "0.02em",
        padding: "2px 7px",
        borderRadius: "4px",
        flexShrink: 0,
        lineHeight: 1.5,
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
      }}
    >
      {method}
    </span>
  );
};

const StepRow = ({ step, chainId, isSelected, onSelect, onDelete, isDeletingTc }) => {
  return (
    <div
      className={`group relative flex items-start gap-2 pl-9 pr-3 py-2 cursor-pointer select-none transition-colors duration-100 ${
        isSelected ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-700"
      }`}
      onClick={() => onSelect(step)}
      title={step.test_case || step.endpoint}
    >
      <span
        className="absolute top-0 bottom-0 w-px"
        style={{ left: "22px", backgroundColor: "#e5e7eb" }}
      />

      <span
        className={`absolute w-[10px] h-[10px] rounded-full border-2 flex-shrink-0 transition-colors ${
          isSelected
            ? "bg-blue-500 border-blue-500"
            : "bg-white border-gray-300 group-hover:border-gray-400"
        }`}
        style={{ left: "17px", top: "50%", transform: "translateY(-50%)" }}
      />

      <MethodPill method={step.http_method} />

      <div className="flex-1 min-w-0">
        <p
          className={`text-[13px] font-medium truncate leading-tight ${
            isSelected ? "text-blue-900" : "text-gray-800"
          }`}
        >
          {step.test_case || step.endpoint}
        </p>

        {step.test_case && (
          <p className="text-[11px] font-mono text-gray-400 truncate leading-tight mt-0.5">
            {step.endpoint}
          </p>
        )}
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(step);
        }}
        disabled={isDeletingTc}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-100 disabled:opacity-30 cursor-pointer flex-shrink-0"
        title="Remove step"
      >
        <Trash2Icon size={13} className="text-red-400" />
      </button>
    </div>
  );
};

const Chain = ({
  chain,
  collections,
  setLoader,
  setActiveTab,
  setResponse5,
  setChainResult,
  setChainTest,
  setEndPoint,
  setUrl,
  setMethod,
  setBody,
  setHeaders,
  onStepClick,
  onRunChain,
  selectedStepKey,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false });

  const queryClient = useQueryClient();
  const notify = useNotification();

  const { mutate: runApiChain, isLoading: isRunning } = useRunApiChain({
    onSuccess: (data) => {
      setChainResult(data);
      setActiveTab("testResults");
      setChainTest("chainResults");
      setLoader(false);
      notify("Chain run complete", "success");
    },
    onError: (error) => {
      setResponse5({ error: "Chain execution failed.", details: error });
      setLoader(false);
      notify(`Chain failed: ${error}`, "error");
    },
  });

  const { mutate: deleteChain, isPending: isDeleting } = useDeleteApiChain({
    onSuccess: () => {
      notify("Chain deleted", "success");
      queryClient.invalidateQueries(["latestAPIResults"]);
    },
    onError: (error) => notify(`Delete failed: ${error}`, "error"),
  });

  const { mutate: deleteTcFromChain, isPending: isDeletingTc } = useDeleteTcFromChain({
    onSuccess: () => {
      notify("Step removed", "success");
      queryClient.invalidateQueries(["latestAPIResults"]);
    },
    onError: (error) => notify(`Remove step failed: ${error}`, "error"),
  });

  const steps = JSON.parse(chain?.steps || "[]");
  const hasSelectedStep = selectedStepKey && selectedStepKey.startsWith(`${chain.id}-`);

  return (
    <>
      <div className="group">
        <div
          className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none transition-colors duration-100 rounded-sm ${
            hasSelectedStep && !isOpen ? "bg-blue-50" : "hover:bg-gray-100"
          }`}
          onClick={() => setIsOpen((v) => !v)}
        >
          <ChevronRight
            size={14}
            className="text-gray-400 flex-shrink-0"
            style={{
              transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.18s ease",
            }}
          />

          <Link2 size={15} className="text-blue-400 flex-shrink-0" />

          <Tooltip title={chain?.name || ""} arrow placement="right" enterDelay={600}>
            <span className="flex-1 text-[15px] font-medium text-gray-800 truncate leading-none cursor-default">
              {chain?.name}
            </span>
          </Tooltip>

          <span className="text-[13px] text-gray-400 flex-shrink-0 tabular-nums bg-gray-100 px-1.5 py-0.5 rounded">
            {steps.length}
          </span>
          <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Run chain" arrow placement="top">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onRunChain) onRunChain(chain);
                  setLoader(true);
                  runApiChain({ chainId: chain.id });
                }}
                disabled={isRunning}
                className="w-6 h-6 flex items-center justify-center rounded hover:bg-green-100 transition-colors cursor-pointer disabled:opacity-40"
              >
                <Play size={13} className="text-green-600" />
              </button>
            </Tooltip>
            <Tooltip title="Delete chain" arrow placement="top">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteDialog({ open: true });
                }}
                className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-100 transition-colors cursor-pointer"
              >
                <Trash2Icon size={13} className="text-red-400" />
              </button>
            </Tooltip>
          </div>
        </div>

        {isOpen && (
          <div className="pb-1">
            {steps.length === 0 ? (
              <p className="pl-9 py-2 text-xs text-gray-400 italic">No steps defined</p>
            ) : (
              steps.map((step, i) => {
                const stepKey = `${chain.id}-${step.step_number}`;
                return (
                  <StepRow
                    key={i}
                    step={step}
                    chainId={chain.id}
                    isSelected={selectedStepKey === stepKey}
                    onSelect={(s) => onStepClick && onStepClick(s, chain)}
                    onDelete={(s) => deleteTcFromChain({ tcId: s.step_number })}
                    isDeletingTc={isDeletingTc}
                  />
                );
              })
            )}
          </div>
        )}
      </div>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: "8px", fontFamily: "inherit" } }}
      >
        <DialogTitle style={{ fontSize: "1rem", fontWeight: 600, paddingBottom: 8 }}>
          Delete Chain
        </DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-600">
            Delete <span className="font-semibold text-gray-900">{chain?.name}</span>? This cannot
            be undone.
          </p>
        </DialogContent>
        <DialogActions style={{ padding: "12px 20px", gap: 8 }}>
          <button
            onClick={() => setDeleteDialog({ open: false })}
            className="px-4 py-2 text-sm text-gray-500 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              deleteChain({ chainId: chain.id });
              setDeleteDialog({ open: false });
            }}
            disabled={isDeleting}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 cursor-pointer transition-colors"
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Chain;
